import { makeStyles } from 'lib/components';

export default makeStyles((theme) => ({
  pageShell: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    width: '100%',
    backgroundColor: theme.evPalette.charcoal[100],
  },
  infoBanner: {
    width: '100%',
    borderRadius: 0,
    margin: 0,
    boxSizing: 'border-box',
  },
  alertLink: {
    color: theme.evPalette.ocean[700],
    fontWeight: 600,
    textDecoration: 'underline',
  },
  // Mirrors orderSubmitted EVPaper boxing
  paper: {
    flex: 1,
    width: '100%',
    padding: 24,
    boxSizing: 'border-box',
    backgroundColor: theme.evPalette.salt[100],
    '@media (min-width: 640px)': {
      margin: '16px 24px',
      width: 'auto',
      borderRadius: 4,
    },
  },
  successTitle: {
    fontWeight: 600,
    fontSize: 20,
    lineHeight: '30px',
    color: theme.evPalette.charcoal[900],
    marginBottom: 8,
  },
  reasonText: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '24px',
    color: theme.evPalette.charcoal[800],
    marginBottom: 24,
  },
  summaryRow: {
    display: 'flex',
    flexDirection: 'column',
    columnGap: 24,
    rowGap: 24,
    flexWrap: 'wrap',
    width: '100%',
    '@media (min-width: 640px)': {
      flexDirection: 'row',
      flexWrap: 'nowrap',
      alignItems: 'stretch',
    },
  },
  summaryMeta: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 26,
    minWidth: 0,
    width: '100%',
  },
  metaItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    '@media (min-width: 1024px)': {
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
  },
  metaLabel: {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: '18px',
    textTransform: 'uppercase',
    color: theme.evPalette.charcoal[800],
    '@media (min-width: 1024px)': {
      minWidth: '40%',
    },
  },
  metaValue: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '24px',
    color: theme.evPalette.charcoal[900],
  },
  metaValueBold: {
    fontWeight: 600,
  },
  costColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    alignItems: 'flex-start',
    width: '100%',
    minWidth: 0,
    flex: 1,
  },
  unitBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 12px',
    borderRadius: '9999px',
    backgroundColor: theme.evPalette.ocean[100],
    color: '#2274F7',
    fontWeight: 600,
    fontSize: 14,
    lineHeight: '20px',
  },
  usageRoot: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    width: '100%',
    minWidth: 0,
    paddingTop: 8,
  },
  usagePlanRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    flexWrap: 'wrap',
  },
  usagePlanName: {
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '18px',
    color: theme.evPalette.charcoal[900],
  },
  usagePlanCount: {
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '18px',
    color: theme.evPalette.charcoal[900],
  },
  // Match order-submitted SubscriptionConsumptionBar: ocean under 100%, marigold at 100%+
  usageProgress: {
    width: '100%',
    height: 6,
    borderRadius: 3,
    backgroundColor: theme.evPalette.charcoal[200],
    '& .myev-MuiLinearProgress-bar': {
      borderRadius: 3,
      backgroundColor: theme.evPalette.ocean[600],
    },
  },
  usageProgressFull: {
    '& .myev-MuiLinearProgress-bar': {
      backgroundColor: `${theme.evPalette.marigold?.[300]
        || theme.evPalette.mango[300]
        || theme.evPalette.mango[700]
        || theme.evPalette.ocean[600]} !important`,
    },
  },
  usageProgressHigh: {
    '& .myev-MuiLinearProgress-bar': {
      // 70–99%: warning (mango) — order-submitted currently only has 2 colors; mid tier for sitemap/design ask
      backgroundColor: `${theme.evPalette.mango[500]
        || theme.evPalette.mango[700]
        || theme.evPalette.ocean[600]} !important`,
    },
  },
  usageCaption: {
    marginTop: 2,
    fontSize: 12,
    lineHeight: '16px',
    color: theme.evPalette.charcoal[700],
  },
  imageColumn: {
    flex: 1,
    minWidth: 0,
    width: '100%',
    display: 'flex',
    alignItems: 'stretch',
  },
  imageContainer: {
    width: '100%',
    minHeight: 220,
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: theme.evPalette.charcoal[200],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
  },
  imageError: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.evPalette.charcoal[700],
    fontSize: 14,
  },
  // Mirrors orderSubmitted buildingsHeading
  detailsHeading: {
    fontWeight: 600,
    fontSize: 16,
    lineHeight: '24px',
    padding: '24px 0 16px 0',
    color: theme.evPalette.charcoal[900],
  },
  tableWrap: {
    border: `1px solid ${theme.evPalette.charcoal[300]}`,
    borderRadius: 4,
    backgroundColor: theme.evPalette.salt[100],
    '& tbody>tr:last-child>td': { border: 'none' },
  },
  tableHeader: {
    '& th': {
      backgroundColor: theme.evPalette.charcoal[100],
      fontWeight: 600,
      fontSize: 16,
      lineHeight: '24px',
      border: 'none',
    },
  },
  tableCell: {
    fontWeight: 400,
    fontSize: 16,
    lineHeight: '24px',
    color: theme.evPalette.charcoal[900],
  },
  mobileDetail: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 8,
    padding: '16px 0 32px 0',
    borderBottom: `1px solid ${theme.evPalette.charcoal[300]}`,
    color: theme.evPalette.charcoal[900],
  },
  mobileProduct: {
    fontWeight: 600,
    fontSize: 14,
    lineHeight: '18px',
  },
  mobileAddress: {
    fontWeight: 400,
    fontSize: 14,
    lineHeight: '24px',
  },
  mobileRow: {
    columnGap: 48,
    '&>div>p': {
      '&:first-child': {
        textTransform: 'uppercase',
        fontWeight: 600,
        fontSize: 12,
        lineHeight: '18px',
        color: theme.evPalette.charcoal[700],
      },
      fontWeight: 400,
      fontSize: 14,
      lineHeight: '24px',
    },
  },
  emptyState: {
    padding: theme.spacing(2),
    color: theme.evPalette.charcoal[700],
  },
  loaderContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 220,
    width: '100%',
  },
}));
